import type { CSSProperties } from 'react';

/**
 * WEB-1686 — КАДР КАРТИНКИ внутри блока: как она вписывается, какая часть
 * видна при обрезке, высота/пропорции кадра и выравнивание.
 *
 * Как было: у картинки не было НИ ОДНОЙ такой настройки — ширина, радиус и
 * отступ зашиты в ветку рендера, полей `objectFit` / `objectPosition` /
 * высоты / пропорций / выравнивания не существовало ни в рендере, ни в
 * описании блока. Картинка всегда растянута во всю ширину блока, обрезать и
 * сдвинуть её было нечем — на подложке блока это и заметил владелец.
 *
 * ЧЕМ ЭТОТ ФАЙЛ НЕ ВЛАДЕЕТ (главный стык карточки):
 *  · `marginBottom: 16` картинки — это РАССТОЯНИЕ МЕЖДУ БЛОКАМИ, владелец
 *    WEB-1680 (`blockSpacing.tsx`): число — умолчание типа `image`, оттуда его
 *    читает справочник и сторож, а при заданной настройке его гасит правило на
 *    `[data-block-spacing]` в global.css. Здесь оно не трогается и не
 *    настраивается — иначе получились бы два слоя, спорящих за один отступ.
 *    Единственное, чем этот файл про него распоряжается (WEB-1709), — НА КАКОМ
 *    УЗЛЕ оно стоит: правило гасит корень блока, а корень у картинки разный в
 *    двух ветках разметки (сам `<img>` без подписи, обёртка с подписью).
 *    Число при этом одно и то же и берётся из одного места.
 *  · внутренний отступ подложки блока — владелец WEB-1678 (`blockPadding*`,
 *    `blockBackgroundStyle.tsx`), он про расстояние от края подложки до
 *    содержимого и относится к ЛЮБОМУ типу блока, не только к картинке.
 *
 * ПОЧЕМУ ВПИСЫВАНИЕ И ВИДИМАЯ ЧАСТЬ ЖИВУТ ТОЛЬКО ВМЕСТЕ С КАДРОМ. `object-fit`
 * и `object-position` описывают, как картинка ложится В ОТВЕДЁННУЮ КОРОБКУ.
 * Без заданной высоты (или пропорций) коробки нет: элемент принимает размер
 * самой картинки, и оба свойства не делают ничего. Настройка, которая молча
 * ничего не меняет, — ровно мёртвое поле из WEB-1677, поэтому панель и
 * показывает их только при заданном кадре.
 *
 * ПОЧЕМУ У КАДРА БЕЗ ВЫБОРА — ОБРЕЗКА. Умолчание браузера для `object-fit` это
 * `fill`: заданная высота ПЛЮЩИТ фотографию, а такого не просил никто. Из двух
 * осмысленных вариантов умолчанием взята обрезка: автор задаёт высоту, чтобы
 * блок стал нужного размера, а не чтобы вокруг снимка появились поля. Выбрать
 * «целиком» он может тут же соседним полем. На «пусто = как было» это не
 * влияет: без заданного кадра ни одно из свойств не пишется вовсе.
 *
 * ВЫРАВНИВАНИЕ — ПРО КАРТИНКУ СВОЕГО РАЗМЕРА. Сегодня у картинки стоит
 * `width: 100%` (WEB-907: раньше был только `maxWidth`, и картинка уже
 * контейнера рендерилась узкой полосой) — растянутую во всю ширину
 * выравнивать не от чего. Поэтому заданное выравнивание возвращает картинке
 * её собственную ширину (с прежним потолком `maxWidth: 100%`) и ставит её к
 * нужному краю. Внутри заданного кадра выравнивать нечего — там за положение
 * снимка отвечает «какая часть видна», и панель выравнивание не показывает.
 */

/** Как картинка вписывается в кадр. */
export type ImageFit = 'contain' | 'cover';
/** Какая часть снимка остаётся видна при обрезке. */
export type ImageFocus = 'center' | 'top' | 'bottom' | 'left' | 'right';
/** Выравнивание картинки своего размера внутри блока. */
export type ImageAlign = 'left' | 'center' | 'right';

/** Умолчание вписывания при заданном кадре — см. шапку файла. */
export const DEFAULT_IMAGE_FIT: ImageFit = 'cover';

/** Пропорции кадра, предлагаемые панелью. Пусто = кадр не задан. */
export const IMAGE_BOX_RATIOS: readonly string[] = [
  '1/1',
  '4/3',
  '3/4',
  '16/9',
  '9/16',
];

export interface ImageFrameFields {
  /** Высота кадра, px. Absent = кадра нет (высота по картинке). */
  imageBoxHeight?: number;
  /** Пропорции кадра. Absent/пусто = кадра нет. Слабее высоты. */
  imageBoxRatio?: string;
  imageFit?: ImageFit;
  imageFocus?: ImageFocus;
  imageAlign?: ImageAlign;
  /**
   * Слово владельца 20.10.2026 — своя ШИРИНА картинки, px (8–2000, потолок —
   * ширина блока). Absent = как было (во всю ширину). С шириной картинка
   * встаёт по `imageAlign`, в том числе внутри заданного кадра высоты.
   */
  imageBoxWidth?: number;
  /**
   * Задание stylepack/image-radius (слово владельца 21.10.2026) — своё
   * скругление углов картинки, px (0–64, 0 — прямые углы). Absent = как было:
   * скругление решает переменная стиль-пака `--radius-image`.
   */
  imageRadius?: number;
}

/**
 * Границы своего скругления — ЗЕРКАЛО канона сервера `IMAGE_LOOK_BOUNDS`
 * (backend/src/ai-funnels/block-schema/image-look.ts); разъезд ловит сторож
 * `image-look.look-ends.spec.ts` — он читает ЭТОТ объект с диска, форму
 * `{ min: N, max: N }` не менять, не обновив его.
 */
export const IMAGE_LOOK_BOUNDS = {
  imageRadius: { min: 0, max: 64 },
} as const;

/** Своё скругление в границах, целым числом px; не число — undefined. */
function ownRadius(fields: ImageFrameFields): number | undefined {
  const raw = fields.imageRadius;
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return undefined;
  const b = IMAGE_LOOK_BOUNDS.imageRadius;
  return Math.round(Math.min(b.max, Math.max(b.min, raw)));
}

/**
 * Обжать ли картинку по снимку. При «целиком» (contain) в кадре снимок
 * занимает только часть коробки, остальное — прозрачные поля; скругление
 * коробки пришлось бы на поля, и углы снимка остались бы прямыми. Поэтому со
 * СВОИМ скруглением коробка уходит: картинка своего размера под потолками
 * кадра (высота кадра или ширина / пропорции), углы скругляются у снимка.
 * Цена: снимок меньше кадра больше не растягивается до кадра. Пропорции без
 * своей ширины потолок высоты в px не дают — там кадр прежний.
 * Без своего скругления — всё как было, поля кадра на месте.
 */
function huggedContainCeiling(
  fields: ImageFrameFields,
): { maxHeight: number } | null {
  if (ownRadius(fields) === undefined) return null;
  if (!hasImageBox(fields)) return null;
  if ((fields.imageFit ?? DEFAULT_IMAGE_FIT) !== 'contain') return null;
  if (typeof fields.imageBoxHeight === 'number') {
    return { maxHeight: fields.imageBoxHeight };
  }
  const ratio = ratioOf(fields);
  if (!ratio || !hasOwnWidth(fields)) return null;
  const [w, h] = ratio.split('/').map((part) => Number(part.trim()));
  if (!(w > 0) || !(h > 0)) return null;
  return { maxHeight: Math.round(((fields.imageBoxWidth as number) * h) / w) };
}

function hasOwnWidth(fields: ImageFrameFields): boolean {
  return typeof fields.imageBoxWidth === 'number';
}

/** Прежний стиль картинки — ровно то, что стояло в ветке рендера до WEB-1686. */
const LEGACY_IMAGE_STYLE: CSSProperties = {
  width: '100%',
  maxWidth: '100%',
  borderRadius: 'var(--radius-image, var(--border-radius-md, 8px))',
  // WEB-1680 владеет этим числом (расстояние между блоками), не эта карточка.
  marginBottom: 16,
  display: 'block',
};

function ratioOf(fields: ImageFrameFields): string | null {
  const raw = fields.imageBoxRatio;
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Задан ли кадр — то есть коробка, В КОТОРУЮ картинка вписывается. Высота
 * сильнее пропорций: считать кадр от двух источников сразу нельзя, они
 * противоречат друг другу молча.
 */
export function hasImageBox(fields: ImageFrameFields): boolean {
  return typeof fields.imageBoxHeight === 'number' || ratioOf(fields) !== null;
}

/**
 * Действует ли выравнивание — ОДНО правило на панель (поле видно) и плеер
 * (снимок прижимается): без кадра, со своей шириной и в кадре «целиком»
 * (слово владельца 20.10.2026). В кадре с обрезкой без своей ширины положением
 * правит «какая часть видна», панель выравнивание прячет — и плеер его не
 * читает: у старых воронок `imageAlign` остаётся в данных после того, как
 * автор задал кадр, и не должен сдвигать снимок без его действий (свежий
 * взгляд волны 4, WEB-3718).
 */
export function imageAlignApplies(fields: ImageFrameFields): boolean {
  return (
    !hasImageBox(fields) || hasOwnWidth(fields) || fields.imageFit === 'contain'
  );
}

/** Поля выравнивания: своя ширина + прижатие к краю. */
function alignMargins(align: ImageAlign): CSSProperties {
  if (align === 'center') return { marginLeft: 'auto', marginRight: 'auto' };
  if (align === 'right') return { marginLeft: 'auto', marginRight: 0 };
  return { marginLeft: 0, marginRight: 'auto' };
}

/**
 * Выравнивание переезжает на ОБЁРТКУ, когда та есть (подпись-пилюля): чип
 * позиционируется от обёртки, и если бы она осталась во всю ширину блока, а
 * картинка встала своим размером у края, подпись повисла бы в пустоте
 * (п.3 карточки). Обёртка обжимается по картинке и сдвигается сама.
 *
 * WEB-1709: вместе с выравниванием на обёртку переезжает и прежнее зашитое
 * `marginBottom` — потому что обёртка и есть КОРНЕВОЙ узел блока в этой ветке
 * разметки. Правило гашения (WEB-1680) достаёт до прямого ребёнка пометки
 * расстояния; оставшись на картинке, число оказывалось внуком и гасить его
 * было некому, а наружу оно всё равно схлопывалось — автор не мог убавить
 * расстояние ниже зашитого. Число тут не НОВОЕ и не второе: оно ровно то же
 * самое, просто стоит на корне обеих веток, а не одной.
 */
export function imageWrapperStyle(fields: ImageFrameFields): CSSProperties {
  const base: CSSProperties = {
    position: 'relative',
    marginBottom: LEGACY_IMAGE_STYLE.marginBottom,
  };
  // Скругление при «целиком» в кадре: картинка обжата по снимку, обёртка —
  // по картинке, к краю по выравниванию (без него — по центру, как снимок
  // стоял в кадре).
  if (huggedContainCeiling(fields)) {
    return {
      ...base,
      width: 'fit-content',
      maxWidth: '100%',
      ...alignMargins(fields.imageAlign ?? 'center'),
    };
  }
  // Своя ширина: обёртка обжимается по картинке и встаёт к краю (без
  // выравнивания — к левому, как лёг бы любой блок своей ширины).
  if (hasOwnWidth(fields)) {
    return {
      ...base,
      width: 'fit-content',
      maxWidth: '100%',
      ...(fields.imageAlign ? alignMargins(fields.imageAlign) : {}),
    };
  }
  if (!fields.imageAlign || hasImageBox(fields)) return base;
  return { ...base, width: 'fit-content', ...alignMargins(fields.imageAlign) };
}

/**
 * Стиль самой картинки. Без единого заданного поля возвращается ПРЕЖНИЙ стиль
 * объявление в объявление — старые воронки не двигаются ни на пиксель.
 *
 * `hasWrapper` — рисуется ли подпись-пилюля: тогда поля выравнивания И прежнее
 * зашитое `marginBottom` несёт обёртка (она же корень блока в этой ветке,
 * WEB-1709), и дублировать их на картинке нельзя: сдвиг применился бы дважды,
 * а расстояние осталось бы недосягаемым для правила гашения.
 */
export function imageFrameStyle(
  fields: ImageFrameFields,
  hasWrapper = false,
): CSSProperties {
  const box = hasImageBox(fields);
  const ratio = ratioOf(fields);
  const align = fields.imageAlign;

  const style: CSSProperties = { ...LEGACY_IMAGE_STYLE };
  if (hasWrapper) delete style.marginBottom;

  // Задание stylepack/image-radius: своё скругление сильнее переменной пака.
  const radius = ownRadius(fields);
  if (radius !== undefined) style.borderRadius = `${radius}px`;

  const hugged = huggedContainCeiling(fields);
  if (hugged) {
    style.width = 'auto';
    style.height = 'auto';
    // WEB-4084: своя ширина — потолок, но не шире блока (шапка поля).
    style.maxWidth = hasOwnWidth(fields)
      ? `min(${fields.imageBoxWidth}px, 100%)`
      : '100%';
    style.maxHeight = hugged.maxHeight;
    if (!hasWrapper) Object.assign(style, alignMargins(align ?? 'center'));
    return style;
  }

  // Слово владельца 20.10.2026: своя ширина картинки (потолок maxWidth 100%
  // остаётся) и прижатие к краю по выравниванию — и в кадре высоты тоже.
  if (hasOwnWidth(fields)) {
    style.width = fields.imageBoxWidth;
    if (align && !hasWrapper) Object.assign(style, alignMargins(align));
  }

  if (box) {
    if (typeof fields.imageBoxHeight === 'number') {
      style.height = fields.imageBoxHeight;
    } else if (ratio) {
      style.aspectRatio = ratio;
    }
    style.objectFit = fields.imageFit ?? DEFAULT_IMAGE_FIT;
    if (fields.imageFocus) {
      style.objectPosition = fields.imageFocus;
    } else if (align && imageAlignApplies(fields)) {
      // Слово владельца 20.10.2026: «Целиком» в кадре центрировал снимок
      // насильно, выравнивание в кадре не действовало вовсе. Своя «видимая
      // часть» сильнее; без неё снимок встаёт по выравниванию — там, где
      // панель выравнивание показывает (imageAlignApplies, волна 4).
      style.objectPosition = align;
    }
    return style;
  }

  if (align && !hasOwnWidth(fields)) {
    // Своя ширина картинки под прежним потолком (WEB-907 оставлен потолком, а
    // не растяжкой: растянутую во всю ширину выравнивать не от чего).
    style.width = 'auto';
    if (!hasWrapper) Object.assign(style, alignMargins(align));
  }

  return style;
}
